import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
// The CEO app's meeting-time helpers, copied byte-for-byte: `meeting_datetime`
// is a wall-clock time with a fake `Z`, so `moment(iso)` shifted it by the
// coach's zone (9:00 AM showed as 2:30 PM in India) while the CEO saw 9:00.
import {
  formatMeetingDateTime,
  isMeetingPast,
  isMeetingToday,
} from "../../../../utils/meetingDateTime";
import {
  ArrowRightIcon,
  CommentComposer,
  CommentIcon,
  CommentList,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import SummaryGroups, {
  FilterChips,
} from "../../StrategyCanvas/SummaryPanel/SummaryPanel";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
import useAnnualOkrReviewStore, {
  useAnnualOkrReviewStoreSelector,
} from "./_redux/AnnualOkrReviewStore";
import {
  buildReviewSummary,
  countItems,
  countWhere,
  filterGroups,
} from "./AnnualOkrReviewSummary";
import ObjectiveRow from "./ObjectiveRow";
import CompleteReviewModal from "./CompleteReviewModal";
import LiveReviewStepperModal from "./LiveReviewStepperModal";
import AnnualOkrReviewSkeleton from "./AnnualOkrReviewSkeleton";
import useAnnualOkrStore from "../_redux/AnnualOkrStore";
import { ANNUAL_OKR_LIVE_REVIEW } from "../../../../data/RouteUrls";
// Canvas's own Review Mode layout (.scrm-*, .rm-*, .rr-*), imported rather than
// re-derived — it is what makes this screen and the Execution Blueprint's the
// same two-panel review by construction. See ObjectiveRow's header note.
import "../../StrategyCanvas/StrategyCanvasReview/ReviewMode/StrategyCanvasReviewMode.scss";
import "./AnnualOkrReview.scss";

// ═══════════════════════════════════════════════════════════════════════════
// Review Mode — left column is the Objectives to review, right column is the
// Review Summary and the send/schedule footer. Same shell as
// `ExecutionBlueprintReviewMode`.
//
// ⚠️ NO BAND / SECTION TREE. EXW wraps its rows in `CanvasBand` because its
// payload has four WIG sections that ARE the grouping. §6.3 returns a flat list
// of Objectives with no grouping at all — `wig_label` is provenance on an
// Objective, not a container, and is null on freestanding ones. Bands built
// from it would put half the board under "Uncategorised", so the rows sit
// directly in the left column.
// ═══════════════════════════════════════════════════════════════════════════

const selectReview = (state) => ({
  reviewByUniqueId: state.reviewByUniqueId,
  reviewLoading: state.reviewLoading,
  startingLiveReview: state.startingLiveReview,
});

// §6.8 accepts either of these as an entry point, so the coach can start early
// rather than waiting for a slot they scheduled. Past LIVE_REVIEW_IN_PROGRESS
// the session is already running and this button would start it twice.
const CAN_START_LIVE_REVIEW = ["COACH_REVIEW_COMPLETED", "LIVE_REVIEW_SCHEDULED"];

// §6.3's `annual_okr_state`. Past this point the review has been sent and the
// screen is a record of it rather than a workspace.
const SUBMITTED_STATES = [
  "COACH_REVIEW_COMPLETED",
  "LIVE_REVIEW_SCHEDULED",
  "LIVE_REVIEW_IN_PROGRESS",
  "LOCKED",
];

// Lifted from EXW verbatim so the footer CTA is the same control.
const FooterActionButton = ({ label, onClick, disabled, disabledReason }) => {
  const button = (
    <button
      type="button"
      className="rm-complete-btn"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {label}
      <ArrowRightIcon />
    </button>
  );
  if (!disabled || !disabledReason) return button;
  return (
    <StandardTooltip tooltTipContent={disabledReason}>
      <span className="rm-btn-tooltip-wrap">{button}</span>
    </StandardTooltip>
  );
};

// `scope_type: "overall"` (§6.5, which takes no `scope_key` for it). Mirrors
// EXW's own OverallCommentPanel — kept local for the same reason theirs is: it
// has no heading or trigger row of its own.
const OverallCommentPanel = ({ comments, onAdd, onDelete, readOnly }) => {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isCommenting, setIsCommenting] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    const saved = await onAdd(draft.trim());
    setSaving(false);
    if (saved) {
      setDraft("");
      setIsCommenting(false);
    }
  };

  const handleDelete = async (commentId) => {
    setDeletingId(commentId);
    await onDelete(commentId);
    setDeletingId(null);
  };

  return (
    <>
      {!readOnly && comments.length === 0 && !isCommenting && (
        <button
          type="button"
          className="rm-overall-trigger"
          onClick={() => setIsCommenting(true)}
        >
          <CommentIcon size={12} />
          Add your overall assessment for the CEO
        </button>
      )}

      {comments.length > 0 && (
        <CommentList
          comments={comments}
          onDelete={handleDelete}
          deletingId={deletingId}
          readOnly={readOnly}
          badge="Overall assessment"
        />
      )}

      {!readOnly && comments.length === 0 && isCommenting && (
        <CommentComposer
          className="rm-section-comment-composer"
          value={draft}
          onChange={setDraft}
          onSave={handleSave}
          saving={saving}
          placeholder="Your overall assessment of the Company OKRs for the CEO"
        />
      )}
    </>
  );
};

const AnnualOkrReviewMode = ({ uniqueId }) => {
  const history = useHistory();
  const { reviewByUniqueId, reviewLoading, startingLiveReview } =
    useAnnualOkrReviewStoreSelector(selectReview);
  const review = reviewByUniqueId[uniqueId];

  const [expandedId, setExpandedId] = useState(null);
  // ⚠️ THE FIRST OBJECTIVE OPENS ITSELF, ONCE. The review used to open with
  // every row collapsed, so the coach's first action on every single review was
  // a click that revealed the thing they came to read.
  //
  // `openedInitial` is a ref, not state: it must not re-trigger a render, and it
  // must survive the row being collapsed again. Without it, closing that first
  // row would immediately re-open it and the accordion could never be fully
  // shut.
  const openedInitial = useRef(false);
  const [summaryFilter, setSummaryFilter] = useState("all");
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  // ⚠️ THIS IS A SNAPSHOT AND MUST NOT BE READ DIRECTLY — use `flaggedPending`
  // below. It records WHICH Objectives were undecided at the moment a send was
  // blocked, and nothing ever removes an id from it, which was a real bug: the
  // highlighted rows kept their error background and the count under the button
  // kept saying "3 Objectives still need a decision" after the coach had
  // accepted two of them. The note claimed to "drop as the coach decides each
  // one"; it never did.
  //
  // It stays a snapshot on purpose — flagging only the rows the coach was
  // actually stopped on, not every undecided row that appears later — so the
  // fix is to intersect it with the CURRENT pending set on every render.
  const [flaggedSnapshotIds, setFlaggedSnapshotIds] = useState(new Set());
  const [showStartLiveReviewModal, setShowStartLiveReviewModal] =
    useState(false);
  const leftRef = useRef(null);

  const isSubmitted = SUBMITTED_STATES.includes(review?.annual_okr_state);
  const canStartLiveReview = CAN_START_LIVE_REVIEW.includes(
    review?.annual_okr_state,
  );
  const objectives = useMemo(() => review?.objectives || [], [review]);

  // Opens the first Objective as soon as the review lands. In an effect rather
  // than in `useState`'s initialiser because `objectives` is empty on the first
  // render — the payload is still in flight.
  useEffect(() => {
    if (openedInitial.current || !objectives.length) return;
    openedInitial.current = true;
    setExpandedId(objectives[0].annual_objective_id);
  }, [objectives]);

  const summaryGroups = useMemo(() => buildReviewSummary(review), [review]);
  const summaryTotal = countItems(summaryGroups);
  const summaryDiscuss = countWhere(
    summaryGroups,
    (item) => item.status === "to_discuss",
  );
  const summaryWithComments = countWhere(
    summaryGroups,
    (item) => (item.comments || []).length > 0,
  );
  const visibleSummaryGroups = useMemo(() => {
    if (summaryFilter === "discuss") {
      return filterGroups(summaryGroups, (item) => item.status === "to_discuss");
    }
    if (summaryFilter === "comments") {
      return filterGroups(
        summaryGroups,
        (item) => (item.comments || []).length > 0,
      );
    }
    return summaryGroups;
  }, [summaryGroups, summaryFilter]);

  // §6.7's gate, computed with the same rule the server enforces.
  const pendingObjectives = useMemo(
    () => objectives.filter((o) => o.review_status == null),
    [objectives],
  );

  // The snapshot ∩ what is STILL undecided. Recomputed from `objectives`, so
  // every accept or to-discuss immediately drops that row's highlight and
  // decrements the note — and the note disappears on the last decision without
  // needing another blocked click to clear it.
  const flaggedPending = useMemo(() => {
    if (!flaggedSnapshotIds.size) return new Set();
    const stillPending = new Set(
      pendingObjectives.map((o) => o.annual_objective_id),
    );
    return new Set(
      [...flaggedSnapshotIds].filter((id) => stillPending.has(id)),
    );
  }, [flaggedSnapshotIds, pendingObjectives]);

  // ⚠️ The click is always allowed and the check happens HERE rather than
  // disabling the button — the same call EXW made, and for the same reason: a
  // disabled button can only count the problem, while this can point at it.
  const handleCompleteClick = () => {
    if (pendingObjectives.length) {
      setFlaggedSnapshotIds(
        new Set(pendingObjectives.map((o) => o.annual_objective_id)),
      );
      const first = pendingObjectives[0].annual_objective_id;
      setExpandedId(first);
      leftRef.current
        ?.querySelector(`[data-review-target="objective-${first}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setFlaggedSnapshotIds(new Set());
    setShowCompleteModal(true);
  };

  // §6.8. Mirrors EXW's `handleStartLiveReview`: the store returns `{ ok }`
  // rather than throwing, so the only decision here is whether to navigate.
  const handleStartLiveReview = async () => {
    const result = await store().startLiveReview(uniqueId);
    if (!result?.ok) return; // The store already surfaced the error.
    setShowStartLiveReviewModal(false);
    // The dashboard's stage and CTA are now stale — this run just moved to
    // LIVE_REVIEW_STARTED. Fire-and-forget: nothing on this path reads it.
    useAnnualOkrStore.getState().fetchDashboard();
    history.push(ANNUAL_OKR_LIVE_REVIEW.replace(":annualOkrId", uniqueId));
  };

  // The booked session, in one of three states (2026-10-09):
  //   upcoming → "Live review scheduled for 9:00 AM on 9th October, 2026."
  //   today    → "Live review is today at 9:00 AM."
  //   missed   → the day has passed and the session never started, so the
  //              coach is told to agree a new time with the CEO offline. Only
  //              reachable while the run is still pre-live-review: once it
  //              starts, `isSubmitted`'s footer is replaced by the session.
  const meeting = (() => {
    const iso = review?.meeting_datetime;
    const when = formatMeetingDateTime(iso, "h:mm A [on] Do MMMM, YYYY");
    if (!when) return null;
    if (isMeetingToday(iso)) {
      return { kind: "today", time: formatMeetingDateTime(iso, "h:mm A") };
    }
    if (isMeetingPast(iso)) return { kind: "missed", when };
    return { kind: "upcoming", when };
  })();

  if (reviewLoading && !review) return <AnnualOkrReviewSkeleton />;

  if (!review) {
    return (
      <div className="aokr-empty">Could not load this review. Try again.</div>
    );
  }

  const store = useAnnualOkrReviewStore.getState;

  return (
    <div className="scrm-container">
      <div className="scrm-left" ref={leftRef}>
        {objectives.length === 0 ? (
          <div className="aokr-empty">
            This client has no confirmed objectives yet.
          </div>
        ) : (
          <div className="rm-section">
            {/* The shared group heading — same rail, name and count as the
                Review Summary beside it, the CEO's remarks panel and the live
                review. It was a plain <h2> here for a few hours; four screens
                labelling one list four ways is how this feature keeps drifting.
                See `_shared/_objective-details.scss`. */}
            <div className="okr-group-head">
              <span className="okr-group-rail" aria-hidden="true" />
              <span className="okr-group-name">Company OKRs</span>
              <span className="okr-group-count">{objectives.length}</span>
            </div>
            <div className="rm-rows">
              {objectives.map((objective) => (
                <ObjectiveRow
                  key={objective.annual_objective_id}
                  uniqueId={uniqueId}
                  objective={objective}
                  expanded={expandedId === objective.annual_objective_id}
                  onToggleExpand={() =>
                    setExpandedId((prev) =>
                      prev === objective.annual_objective_id
                        ? null
                        : objective.annual_objective_id,
                    )
                  }
                  readOnly={isSubmitted}
                  pending={flaggedPending.has(objective.annual_objective_id)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="rm-overall">
          <div className="rm-overall-comments">
            <OverallCommentPanel
              comments={review.overall_comments || []}
              onAdd={(text) =>
                store().addComment(uniqueId, { scopeType: "overall", text })
              }
              onDelete={(commentId) => store().deleteComment(uniqueId, commentId)}
              readOnly={isSubmitted}
            />
          </div>
        </div>
      </div>

      <div className="scrm-right">
        <div className="rm-summary-header">
          <span className="rm-summary-header-title">Review Summary</span>
        </div>
        <div className="rm-summary-header-divider" />

        {summaryTotal > 0 && (
          <div className="rm-summary-filters">
            <FilterChips
              value={summaryFilter}
              onChange={setSummaryFilter}
              options={[
                { key: "all", label: "All", count: summaryTotal },
                { key: "discuss", label: "To discuss", count: summaryDiscuss },
                {
                  key: "comments",
                  label: "With comments",
                  count: summaryWithComments,
                },
              ]}
            />
          </div>
        )}

        <div className="rm-right-body">
          {summaryTotal === 0 ? (
            <div className="rm-summary-empty">Your review will appear here.</div>
          ) : (
            <SummaryGroups
              groups={visibleSummaryGroups}
              emptyLabel="Nothing matches this filter."
            />
          )}
        </div>

        {/* One slot, two states — the CTA swaps in place rather than the footer
            growing a second button, exactly as EXW and Canvas do it. */}
        <div className="rm-right-footer">
          {isSubmitted ? (
            <>
              {/* One slot, two states — the CTA swaps in place rather than the
                  footer growing a second button, exactly as EXW and Canvas do
                  it. Once the review is in, "Complete review & send to CEO" has
                  nothing left to do, so Start Live Review takes its place as
                  the only action on the screen.

                  ⚠️ Gated on the STATE, not just on `isSubmitted`. §6.8 accepts
                  COACH_REVIEW_COMPLETED or LIVE_REVIEW_SCHEDULED; once the run
                  is LIVE_REVIEW_IN_PROGRESS the session is already running and
                  this would start it twice, and once LOCKED there is nothing to
                  start at all. */}
              {canStartLiveReview ? (
                <FooterActionButton
                  label="Start Live Review"
                  onClick={() => setShowStartLiveReviewModal(true)}
                />
              ) : null}
              <div
                className={`rm-meeting-scheduled${
                  meeting?.kind === "missed" ? " rm-meeting-scheduled--missed" : ""
                }`}
              >
                <span>This review has been sent to the CEO.</span>
                {meeting?.kind === "upcoming" ? (
                  <strong>Live review scheduled for {meeting.when}.</strong>
                ) : null}
                {meeting?.kind === "today" ? (
                  <strong className="rm-meeting-today">
                    <i className="fa-solid fa-calendar-day" aria-hidden="true" />
                    Live review is today at {meeting.time}.
                  </strong>
                ) : null}
                {meeting?.kind === "missed" ? (
                  <span className="rm-meeting-missed" role="status">
                    <strong>
                      <i
                        className="fa-solid fa-calendar-xmark"
                        aria-hidden="true"
                      />
                      Live review was scheduled for {meeting.when}
                    </strong>
                    The session hasn&apos;t taken place. Please align with the
                    CEO and reschedule the live review offline.
                  </span>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <FooterActionButton
                label="Complete review & send to CEO"
                onClick={handleCompleteClick}
              />
              {/* Shown only after a blocked attempt. Counts `flaggedPending`,
                  which is the snapshot intersected with what is still
                  undecided — so it really does drop as the coach decides each
                  one, and vanishes on the last. Reading the raw snapshot here
                  was the bug. `role="alert"` because it appears in response to
                  an action and is the only text explaining why nothing
                  happened. */}
              {flaggedPending.size > 0 && (
                <p className="rm-pending-note" role="alert">
                  {flaggedPending.size === 1
                    ? "1 Objective still needs a decision before you can send this — accept it, or flag it to discuss."
                    : `${flaggedPending.size} Objectives still need a decision before you can send this — accept each one, or flag it to discuss.`}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {showStartLiveReviewModal && (
        <LiveReviewStepperModal
          onNext={handleStartLiveReview}
          onBack={() => setShowStartLiveReviewModal(false)}
          // `onNext` performs a real POST, so the modal has to be able to show
          // that and to stop a second click sending a second request. Back
          // stays enabled: cancelling a slow start is reasonable, and the
          // request is idempotent enough that a 409 on the retry resolves.
          nextLoading={startingLiveReview}
        />
      )}

      {showCompleteModal && (
        <CompleteReviewModal
          uniqueId={uniqueId}
          totalObjectives={review.total_objectives}
          acceptedCount={review.accepted_count}
          toDiscussCount={review.to_discuss_count}
          commentCount={review.comment_count}
          onClose={() => setShowCompleteModal(false)}
        />
      )}
    </div>
  );
};

export default AnnualOkrReviewMode;
